// API Gateway Migration Utility
// Helps components transition from Legacy Lambda URLs to API Gateway

import { API_GATEWAY_CONFIG, API_GATEWAY_ENDPOINTS, apiGatewayFetch } from '../config/apiGatewayConfig';
import { API_ENDPOINTS } from '../config/apiRouting';

// Services that are currently deployed in API Gateway
// CORS is now handled properly by Lambdas detecting API Gateway vs Lambda URL
const API_GATEWAY_SERVICES = {
  coreData: true,       // Enterprises, concepts, franchisees, categories
  stores: true,         // Store management
  employees: true,      // Employee management
  scheduling: true,     // Scheduling
  discounts: true,      // Discounts
  socialMedia: true,    // Social media
  categories: true      // Categories (part of coreData)
};

// Services still using Legacy Lambda URLs (due to API Gateway route issues, not CORS)
const LEGACY_SERVICES = {
  aiImages: true,       // Path duplication issues with API Gateway
  menuManagement: true, // /complete-description route issues
  taxManagement: true,
  dayparts: true,       // API Gateway route issues
  securityAudit: true,
  contactForm: true,
  ubiquitiNetwork: true,
  messaging: true,
  menuBoards: true,     // 502 errors
  posSync: true,
  tableCharts: true,    // Route issues
  weather: true,        // Route issues
  waitlist: true,       // Route issues
  payment: true,
  aiChat: true
};

/**
 * Smart API router that automatically chooses the right endpoint
 */
export const smartApiCall = async (
  endpointType: string,
  ...params: any[]
): Promise<Response> => {
  
  // Determine if this endpoint should use API Gateway or Legacy Lambda
  const useApiGateway = shouldUseApiGateway(endpointType);
  
  if (useApiGateway) {
    const url = getApiGatewayEndpoint(endpointType, ...params);
    return await apiGatewayFetch(url);
  } else {
    const url = getLegacyEndpoint(endpointType, ...params);
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    const authHeader = await getAuthHeader();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }
    
    return await fetch(url, { headers });
  }
};

/**
 * Determine if an endpoint should use API Gateway
 */
function shouldUseApiGateway(endpointType: string): boolean {
  // Check if this endpoint type is in our API Gateway services
  const serviceMap: Record<string, string> = {
    // Core Data endpoints
    'enterprises': 'coreData',
    'enterprise': 'coreData',
    'concepts': 'coreData', 
    'franchisees': 'coreData',
    
    // Menu Management endpoints
    'menuItems': 'menuManagement',
    'menuItem': 'menuManagement',
    'generateImage': 'menuManagement',
    
    // Categories endpoints
    'categories': 'coreData',
    
    // Stores endpoints
    'stores': 'stores',
    'store': 'stores',
    'enterpriseStores': 'stores',
    
    // Employees endpoints
    'employees': 'employees',
    'employee': 'employees',
    'jobs': 'employees',
    
    // Scheduling endpoints
    'schedules': 'scheduling',
    'schedule': 'scheduling'
  };
  
  const service = serviceMap[endpointType];
  
  // Check if service is explicitly marked as legacy
  if (service && LEGACY_SERVICES[service as keyof typeof LEGACY_SERVICES]) {
    return false;
  }
  
  return service ? API_GATEWAY_SERVICES[service as keyof typeof API_GATEWAY_SERVICES] : false;
}

/**
 * Get API Gateway endpoint for a given type
 */
function getApiGatewayEndpoint(endpointType: string, ...params: any[]): string {
  const endpoints = API_GATEWAY_ENDPOINTS as any;
  
  if (typeof endpoints[endpointType] === 'function') {
    return endpoints[endpointType](...params);
  }
  
  throw new Error(`API Gateway endpoint not found: ${endpointType}`);
}

/**
 * Get Legacy Lambda endpoint for a given type
 */
function getLegacyEndpoint(endpointType: string, ...params: any[]): string {
  const endpoints = API_ENDPOINTS as any;
  
  if (typeof endpoints[endpointType] === 'function') {
    return endpoints[endpointType](...params);
  }
  
  throw new Error(`Legacy endpoint not found: ${endpointType}`);
}

/**
 * Get authentication header from storage or token getter
 */
async function getAuthHeader(): Promise<string | null> {
  // Try to get token from the API Gateway config (proper auth context)
  let token = null;
  if (API_GATEWAY_CONFIG.tokenGetter) {
    try {
      token = await API_GATEWAY_CONFIG.tokenGetter();
    } catch (error) {
      console.warn('Failed to get auth token from token getter:', error);
    }
  }
  
  // Fallback to localStorage/sessionStorage (for backward compatibility)
  if (!token) {
    token = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
  }
  
  return token ? `Bearer ${token}` : null;
}

/**
 * Wrapper for common API patterns - GET request
 */
export const apiGet = async (endpointType: string, ...params: any[]) => {
  const response = await smartApiCall(endpointType, ...params);
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`API GET failed (${response.status}): ${error}`);
  }
  
  return await response.json();
};

/**
 * Wrapper for common API patterns - POST request
 */
export const apiPost = async (endpointType: string, data: any, ...params: any[]) => {
  const useApiGateway = shouldUseApiGateway(endpointType);
  
  if (useApiGateway) {
    const url = getApiGatewayEndpoint(endpointType, ...params);
    return await apiGatewayFetch(url, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  } else {
    const url = getLegacyEndpoint(endpointType, ...params);
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    const authHeader = await getAuthHeader();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }
    
    return await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
  }
};

/**
 * Wrapper for common API patterns - PUT request
 */
export const apiPut = async (endpointType: string, data: any, ...params: any[]) => {
  const useApiGateway = shouldUseApiGateway(endpointType);
  
  if (useApiGateway) {
    const url = getApiGatewayEndpoint(endpointType, ...params);
    return await apiGatewayFetch(url, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  } else {
    const url = getLegacyEndpoint(endpointType, ...params);
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    const authHeader = await getAuthHeader();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }
    
    return await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    });
  }
};

/**
 * Wrapper for common API patterns - DELETE request
 */
export const apiDelete = async (endpointType: string, ...params: any[]) => {
  const useApiGateway = shouldUseApiGateway(endpointType);
  
  if (useApiGateway) {
    const url = getApiGatewayEndpoint(endpointType, ...params);
    return await apiGatewayFetch(url, {
      method: 'DELETE'
    });
  } else {
    const url = getLegacyEndpoint(endpointType, ...params);
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    const authHeader = await getAuthHeader();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }
    
    return await fetch(url, {
      method: 'DELETE',
      headers
    });
  }
};

/**
 * Debug utility to show which services are using which endpoints
 */
export const getApiStatus = () => {
  return {
    apiGateway: {
      baseUrl: API_GATEWAY_CONFIG.baseUrl,
      services: Object.keys(API_GATEWAY_SERVICES).filter(s => API_GATEWAY_SERVICES[s as keyof typeof API_GATEWAY_SERVICES])
    },
    legacy: {
      services: Object.keys(LEGACY_SERVICES).filter(s => LEGACY_SERVICES[s as keyof typeof LEGACY_SERVICES])
    }
  };
};